import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ReferenceArea } from 'recharts';
import { Download } from 'lucide-react';
import { MealImpact } from '@/hooks/useExperimentResults';
import { useExperimentParticipants } from '@/hooks/useExperimentParticipants';
import { downloadGlucoseDataCSV, downloadCSV } from '@/lib/csvDownload';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import MiniGlucoseChart from '@/components/MiniGlucoseChart';
import { validateExperiment } from '@/lib/experimentValidation';
interface ExperimentResultsListProps {
  results: MealImpact[];
  linkedProducts?: Array<{
    id: string;
    name: string;
    image_url?: string;
  }>;
  experimentId?: string;
  experimentName?: string;
}
const ProductImages: React.FC<{
  foodLogNames: string[];
  linkedProducts?: Array<{
    id: string;
    name: string;
    image_url?: string;
  }>;
}> = ({
  foodLogNames,
  linkedProducts
}) => {
  // Sort food log names alphabetically for consistent ordering
  const sortedFoodLogNames = [...foodLogNames].sort((a, b) => a.localeCompare(b));
  if (!linkedProducts || linkedProducts.length === 0) {
    return <Tooltip delayDuration={50}>
        <TooltipTrigger asChild>
          <div className="flex flex-col gap-1 cursor-help">
            {sortedFoodLogNames.slice(0, 2).map((name, index) => <div key={index} className="text-xs text-gray-600 truncate max-w-32">
                {name}
              </div>)}
            {sortedFoodLogNames.length > 2 && <div className="text-xs text-gray-400">+{sortedFoodLogNames.length - 2} more</div>}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="text-xs">
            <div className="font-medium mb-1">All products:</div>
            {sortedFoodLogNames.map((name, index) => <div key={index}>{name}</div>)}
          </div>
        </TooltipContent>
      </Tooltip>;
  }

  // Try to match food log names with linked products
  const matchedProducts = sortedFoodLogNames.map(name => linkedProducts.find(product => product.name.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(product.name.toLowerCase()))).filter(Boolean);
  return <Tooltip delayDuration={50}>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-2 cursor-help">
          {matchedProducts.slice(0, 3).map((product, index) => <div key={index} className="flex items-center gap-1">
              {product?.image_url && <img src={product.image_url} alt={product.name} className="w-6 h-6 rounded object-cover" />}
              <span className="text-xs text-gray-600 truncate max-w-24">
                {product?.name}
              </span>
            </div>)}
          {sortedFoodLogNames.length > matchedProducts.length && <div className="text-xs text-gray-400">
              +{sortedFoodLogNames.length - matchedProducts.length} more
            </div>}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="text-xs">
          <div className="font-medium mb-1">All products:</div>
          {sortedFoodLogNames.map((name, index) => <div key={index}>{name}</div>)}
        </div>
      </TooltipContent>
    </Tooltip>;
};
const ExperimentResultsList: React.FC<ExperimentResultsListProps> = ({
  results,
  linkedProducts,
  experimentId,
  experimentName
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const getFromLabel = (path: string) => {
    if (path.startsWith('/dashboard/experiments')) return 'experiment details';
    if (path.startsWith('/dashboard/tastermonials')) return 'tastermonials';
    if (path === '/dashboard') return 'dashboard';
    if (path.startsWith('/dashboard/products')) return 'products';
    if (path.startsWith('/dashboard/participants')) return 'participants';
    return 'previous page';
  };
  // Participants for completed_at mapping
  const {
    data: participants = []
  } = useExperimentParticipants(experimentId || '');
  const participantsByUserExperimentId = React.useMemo(() => {
    const m = new Map<string, unknown>();
    participants.forEach(p => m.set(p.userExperimentId, p));
    return m;
  }, [participants]);
  const participantsByUserId = React.useMemo(() => {
    const m = new Map<string, unknown>();
    participants.forEach(p => m.set(p.userId, p));
    return m;
  }, [participants]);
  const getResultCompletedAt = (r: MealImpact) => r.experiment_timestamp_local;
  // Group and sort results by participant
  const resultsWithParticipants = React.useMemo(() => {
    // Create a map of unique participant IDs
    const participantMap = new Map<string, number>();
    let participantCounter = 1;

    // Sort results by user_id first, then by timestamp
    const sortedResults = [...results].sort((a, b) => {
      // First sort by user_id
      if (a.user_id !== b.user_id) {
        return a.user_id.localeCompare(b.user_id);
      }
      // Then by timestamp within same participant
      return new Date(a.adjusted_meal_timestamp_utc).getTime() - new Date(b.adjusted_meal_timestamp_utc).getTime();
    });

    // Assign participant numbers
    return sortedResults.map(result => {
      if (!participantMap.has(result.user_id)) {
        participantMap.set(result.user_id, participantCounter++);
      }
      return {
        ...result,
        participantNumber: participantMap.get(result.user_id)!
      };
    });
  }, [results]);

  // Define a palette of 16 colors for participants
  const participantColors = ['bg-blue-500 text-white', 'bg-green-500 text-white', 'bg-purple-500 text-white', 'bg-red-500 text-white', 'bg-yellow-500 text-black', 'bg-pink-500 text-white', 'bg-indigo-500 text-white', 'bg-orange-500 text-white', 'bg-teal-500 text-white', 'bg-cyan-500 text-white', 'bg-lime-500 text-black', 'bg-rose-500 text-white', 'bg-violet-500 text-white', 'bg-emerald-500 text-white', 'bg-amber-500 text-black', 'bg-slate-500 text-white'];
  const getParticipantColor = (participantNumber: number) => {
    return participantColors[(participantNumber - 1) % participantColors.length];
  };
  const getGlycemicBadgeColor = (glycemic: number | string) => {
    // Convert number to string representation
    let glycemicStr = '';
    if (typeof glycemic === 'number') {
      // Map numeric values to categories (adjust ranges as needed)
      if (glycemic <= 0.3) glycemicStr = 'low';else if (glycemic <= 0.7) glycemicStr = 'medium';else glycemicStr = 'high';
    } else {
      glycemicStr = glycemic?.toLowerCase() || '';
    }
    switch (glycemicStr) {
      case 'low':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  const formatTimestamp = (timestamp: string, timezone?: string) => {
    const date = new Date(timestamp);
    let timeZone = 'UTC'; // Default fallback

    // Handle timezone - if it's a number like "-7", convert or fallback to UTC
    if (timezone) {
      // Check if timezone is a valid IANA timezone identifier
      try {
        // Test if the timezone is valid by trying to use it
        new Intl.DateTimeFormat('en', {
          timeZone: timezone
        }).format(date);
        timeZone = timezone;
      } catch (error) {
        // If timezone is invalid (like "-7"), fallback to UTC
        timeZone = 'UTC';
      }
    }
    const dateStr = date.toLocaleDateString(undefined, {
      timeZone
    });
    const timeStr = date.toLocaleTimeString(undefined, {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/:/g, '.').replace(/\s+(AM|PM)/i, '$1');
    return <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            <div className="text-sm font-medium">{timeStr}</div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm max-w-xs">The time of the meal is shown in the participants local time to indicate relation to circadian rhythm</p>
        </TooltipContent>
      </Tooltip>;
  };
  const sanitizeFilename = (name?: string) => name ? name.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') : 'experiment_results';
  const handleDownloadCombinedCSV = () => {
    const headers = ['participant_number', 'tier', 'age_group', 'gender', 'ethnicity', 'bmi_group', 'tags', 'completed_at', 'meal_timestamp_utc', 'timezone', 'food_log_names', 'start_glucose_level', 'peak_glucose_level', 'time_to_peak', 'time_to_return', 'iauc', 'indicative_glycemic', 'glycemic_range', 'experiment_valid', 'satiety_score', 'Glucose -30m to +120m', 'Baseline -30m to +120m'];
    const toSeriesString = (samples?: number[][]) => {
      if (!samples || samples.length === 0) return '';
      // Determine starting glucose as the value closest to time 0
      const startingSampleOffset = Math.min(...samples.map(s => Math.abs(s[0])));
      const startingGlucose = samples.find(s => Math.abs(s[0]) === startingSampleOffset)?.[1] ?? 0;
      const minSec = -30 * 60;
      const maxSec = 120 * 60;
      return samples.filter(s => s[0] >= minSec && s[0] <= maxSec).map(s => {
        const minutes = s[0] / 60;
        const minutesFormatted = Number.isInteger(minutes) ? minutes : parseFloat(minutes.toFixed(1));
        const delta = parseFloat((s[1] - startingGlucose).toFixed(2));
        return `${minutesFormatted}:${delta}`;
      }).join('; ');
    };
    const rows = resultsWithParticipants.map((r: MealImpact & {
      participantNumber?: number;
    }) => {
      const p = participantsByUserId.get(r.user_id) as {
        tier?: string;
        ageGroup?: string;
        gender?: string;
        ethnicity?: string;
        bmiGroup?: string;
        tags?: string[];
        completedAt?: string;
      } | undefined;
      const isDiabetic = p?.tags?.includes('Diabetic') || false;
      const validation = validateExperiment(r, isDiabetic);
      const glucoseSeries = toSeriesString(r.glucose_samples);
      const baselineSeries = toSeriesString(r.baseline_glucose_samples);
      return [r.participantNumber ?? '', p?.tier ?? '', p?.ageGroup ?? '', p?.gender ?? '', p?.ethnicity ?? '', p?.bmiGroup ?? '', Array.isArray(p?.tags) ? p.tags.join('; ') : '', p?.completedAt ?? '', r.meal_timestamp_utc ?? '', r.timezone ?? '', Array.isArray(r.food_log_names) ? r.food_log_names.join('; ') : '', r.start_glucose_level ?? '', r.peak_glucose_level ?? '', r.time_to_peak ?? '', r.time_to_return ?? '', r.iauc ?? '', r.indicative_glycemic ?? '', r.glycemic_range ?? '', validation.isValid ? 'true' : 'false', r.satiety_score ?? '', glucoseSeries, baselineSeries];
    });
    const base = sanitizeFilename(experimentName);
    downloadCSV({
      data: rows,
      headers,
      filename: `${base}.csv`
    });
  };
  return <TooltipProvider>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Individual Experiment Results</CardTitle>
          <CardDescription>
            Logged meal impacts and glucose responses ({results.length} results)
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadCombinedCSV} title="Download combined results CSV">
          <Download className="h-4 w-4 mr-2" />
          Download
        </Button>
      </CardHeader>
      <CardContent>
        {results.length === 0 ? <div className="text-center py-6 text-muted-foreground">
            No experiment results available yet.
          </div> : <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Participant</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">Local time
                    </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-sm max-w-xs">The time of the meal is shown in the participants local time to indicate relation to circadian rhythm</p>
                    </TooltipContent>
                  </Tooltip>
                </TableHead>
                <TableHead>Chart</TableHead>
                <TableHead>Start (mg/dL)</TableHead>
                <TableHead>Peak (mg/dL)</TableHead>
                <TableHead>Satiety</TableHead>
                <TableHead>iAUC</TableHead>
                <TableHead>Glycemic Impact</TableHead>
                <TableHead>Valid</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resultsWithParticipants.map(result => <TableRow key={result.meal_id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/dashboard/participants/${result.user_id}`, {
              state: {
                from: location.pathname,
                fromLabel: getFromLabel(location.pathname)
              }
            })}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${getParticipantColor(result.participantNumber)}`}>
                        #{result.participantNumber}
                      </div>
                      {/* Show diabetic label only if participant has diabetic tag */}
                      {(participantsByUserId.get(result.user_id) as {
                    tags?: string[];
                  } | undefined)?.tags?.includes('Diabetic') && <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 text-xs">
                          Diabetic
                        </Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ProductImages foodLogNames={result.food_log_names} linkedProducts={linkedProducts} />
                  </TableCell>
                  <TableCell>
                    {new Date(getResultCompletedAt(result)).toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    }).replace(/:/g, '.').replace(/\s+(AM|PM)/i, '$1')}
                  </TableCell>
                  <TableCell>
                    <MiniGlucoseChart samples={result.glucose_samples} startGlucose={result.start_glucose_level} baselineSamples={result.baseline_glucose_samples} />
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {result.start_glucose_level != null ? `${result.start_glucose_level}` : 'N/A'}
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {result.peak_glucose_level}
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {result.satiety_score ? <>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {result.satiety_score}/5
                        </Badge>
                        <div className="text-xs text-gray-500 mt-1">
                          {result.satiety_score === 1 ? 'Very hungry' : result.satiety_score === 2 ? 'Hungry' : result.satiety_score === 3 ? 'Neutral' : result.satiety_score === 4 ? 'Satisfied' : result.satiety_score === 5 ? 'Very full' : 'Unknown'}
                        </div>
                      </> : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {result.iauc !== null && result.iauc !== undefined ? result.iauc.toFixed(1) : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell>
                    {(() => {
                  // Show N/A if glycemic is 0.0 and experiment is invalid or missing start data
                  if (result.indicative_glycemic === 0.0 && (!result.experiment_valid || result.start_glucose_level == null)) {
                    return <span className="text-sm font-medium">N/A</span>;
                  }
                  return <Badge variant="outline" className={getGlycemicBadgeColor(result.indicative_glycemic)}>
                          {typeof result.indicative_glycemic === 'number' ? result.indicative_glycemic.toFixed(2) : result.indicative_glycemic || 'Unknown'}
                        </Badge>;
                })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                  const participant = participantsByUserId.get(result.user_id) as {
                    tags?: string[];
                  } | undefined;
                  const isDiabetic = participant?.tags?.includes('Diabetic') || false;
                  const validation = validateExperiment(result, isDiabetic);
                  if (validation.isValid) {
                    return <Badge variant="default">
                            Valid
                          </Badge>;
                  } else {
                    return <Tooltip>
                            <TooltipTrigger asChild>
                              <div onClick={e => e.stopPropagation()}>
                                <Badge variant="secondary" className="cursor-help">
                                  Invalid
                                </Badge>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-sm max-w-xs">{validation.reason}</p>
                            </TooltipContent>
                          </Tooltip>;
                  }
                })()}
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>}
      </CardContent>
    </Card>
  </TooltipProvider>;
};
export default ExperimentResultsList;